import React, { useEffect, useState } from 'react';
import Layout from '../components/shared/Layout';
import UpcomingEvents from '../components/shared/UpcomingEvents';
import { employeeAPI } from '../services/api';
import { format, formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  Palmtree, Clock, CheckCircle2, ClipboardList,
  Target, TrendingUp, UserCheck, Sprout, Trees, Trophy,
  CalendarCheck, ChevronRight,
} from 'lucide-react';

const statusBadge = (s) => (
  <span className={`badge badge-${s}`}>
    {s === 'pending' ? 'Pendiente' : s === 'approved' ? 'Aprobado' :
     s === 'rejected' ? 'Rechazado' : 'Cancelado'}
  </span>
);

export default function DashboardPage() {
  const [profile,  setProfile]  = useState(null);
  const [history,  setHistory]  = useState({ vacations: [], leaves: [] });
  const [accruals, setAccruals] = useState([]);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    Promise.all([
      employeeAPI.getMyProfile(),
      employeeAPI.getMyHistory(),
      employeeAPI.getMyAccruals(),
    ]).then(([pRes, hRes, aRes]) => {
      setProfile(pRes.data);
      setHistory(hRes.data);
      setAccruals(aRes.data);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <Layout title="Mi panel">
      <div style={{ padding: 48, textAlign: 'center', color: 'var(--gray-400)' }}>Cargando…</div>
    </Layout>
  );

  const available = profile ? profile.vacation_days_total - profile.vacation_days_used : 0;
  const usedPct   = profile ? Math.min((profile.vacation_days_used / Math.max(profile.vacation_days_total, 1)) * 100, 100) : 0;
  const barClass  = usedPct > 80 ? 'danger' : usedPct > 60 ? 'warn' : '';
  const recentVac   = history.vacations.slice(0, 5);
  const recentLeave = history.leaves.slice(0, 3);

  const nextAnniversary = (() => {
    if (!profile?.hire_date) return null;
    const hire  = new Date(profile.hire_date);
    const today = new Date();
    const next  = new Date(today.getFullYear(), hire.getMonth(), hire.getDate());
    if (next <= today) next.setFullYear(today.getFullYear() + 1);
    const diffDays = Math.ceil((next - today) / (1000 * 60 * 60 * 24));
    let yearsCompleted = next.getFullYear() - hire.getFullYear();
    const daysNext = yearsCompleted <= 5 ? 12 : yearsCompleted <= 10 ? 18 : 30;
    return { date: next, daysUntil: diffDays, yearsCompleted, daysNext };
  })();

  const accrualIcon = (years) => {
    if (years <= 1) return <Sprout size={18} color="var(--success)" />;
    if (years <= 5) return <Sprout size={18} color="var(--primary)" />;
    if (years <= 10) return <Trees size={18} color="var(--success)" />;
    return <Trophy size={18} color="var(--warning)" />;
  };

  return (
    <Layout title="Mi panel">
      <div className="page-header">
        <div>
          <div className="page-title">Hola, {profile?.first_name} 👋</div>
          <div className="page-subtitle">
            {format(new Date(), "EEEE d 'de' MMMM, yyyy", { locale: es })}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card blue">
          <Palmtree size={26} strokeWidth={1.5} color="var(--primary)" />
          <div className="stat-value">{available}</div>
          <div className="stat-label">Días disponibles</div>
          <div className="days-bar-wrap">
            <div style={{ fontSize: 11, color: 'var(--gray-400)', marginBottom: 4 }}>
              {profile?.vacation_days_used} usados · {profile?.vacation_days_total} total
            </div>
            <div className="days-bar-track">
              <div className={`days-bar-fill ${barClass}`} style={{ width: `${usedPct}%` }} />
            </div>
          </div>
        </div>

        <div className="stat-card yellow">
          <Clock size={26} strokeWidth={1.5} color="var(--warning)" />
          <div className="stat-value">{history.vacations.filter(v => v.status === 'pending').length}</div>
          <div className="stat-label">Solicitudes pendientes</div>
        </div>

        <div className="stat-card green">
          <CheckCircle2 size={26} strokeWidth={1.5} color="var(--success)" />
          <div className="stat-value">{history.vacations.filter(v => v.status === 'approved').length}</div>
          <div className="stat-label">Vacaciones aprobadas</div>
        </div>

        <div className="stat-card gray">
          <ClipboardList size={26} strokeWidth={1.5} color="var(--gray-500)" />
          <div className="stat-value">{history.leaves.length}</div>
          <div className="stat-label">Permisos solicitados</div>
        </div>
      </div>
      <UpcomingEvents />
      {/* Próximo aniversario */}
      {nextAnniversary && (
        <div className="card section-gap" style={{
          borderColor: nextAnniversary.daysUntil <= 7 ? '#86efac' : 'var(--gray-200)',
          background: nextAnniversary.daysUntil <= 7 ? '#f0fdf4' : 'white',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{
                width: 48, height: 48, borderRadius: 12,
                background: nextAnniversary.daysUntil <= 7 ? 'var(--success)' : 'var(--primary)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Target size={22} color="white" strokeWidth={1.8} />
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 15, color: 'var(--gray-900)' }}>
                  {nextAnniversary.daysUntil === 0
                    ? '¡Hoy cumples un año más en la empresa!'
                    : nextAnniversary.daysUntil <= 7
                    ? `Tu aniversario es en ${nextAnniversary.daysUntil} día${nextAnniversary.daysUntil > 1 ? 's' : ''}`
                    : `Próximo aniversario: ${format(nextAnniversary.date, "d 'de' MMMM, yyyy", { locale: es })}`
                  }
                </div>
                <div style={{ fontSize: 13, color: 'var(--gray-500)', marginTop: 2 }}>
                  Al cumplir {nextAnniversary.yearsCompleted} año{nextAnniversary.yearsCompleted > 1 ? 's' : ''} se acreditarán{' '}
                  <strong style={{ color: 'var(--primary)' }}>+{nextAnniversary.daysNext} días hábiles</strong>
                </div>
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 26, fontWeight: 700, color: nextAnniversary.daysUntil <= 7 ? 'var(--success)' : 'var(--primary)' }}>
                {nextAnniversary.daysUntil === 0 ? '—' : `${nextAnniversary.daysUntil}d`}
              </div>
              <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>
                {nextAnniversary.daysUntil === 0 ? 'hoy' : 'faltan'}
              </div>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Historial de acumulaciones */}
        <div className="card">
          <div className="card-header">
            <div>
              <div className="card-title">Historial de vacaciones generadas</div>
              <div className="card-subtitle">Acumulación anual por antigüedad</div>
            </div>
            <CalendarCheck size={18} color="var(--gray-400)" />
          </div>
          {accruals.length === 0 ? (
            <div className="empty-state" style={{ padding: '24px 0' }}>
              <TrendingUp size={36} color="var(--gray-300)" style={{ margin: '0 auto 10px', display: 'block' }} />
              <div className="empty-state-title">Sin acumulaciones aún</div>
              <div style={{ fontSize: 13 }}>Los días se generan al cumplir cada año de trabajo</div>
              {profile?.hire_date && (
                <div style={{ marginTop: 10, padding: '8px 14px', background: 'var(--primary-light)', borderRadius: 8, fontSize: 12, color: 'var(--primary)' }}>
                  Ingreso: {format(new Date(profile.hire_date), "d 'de' MMMM, yyyy", { locale: es })}
                </div>
              )}
            </div>
          ) : (
            <div>
              {accruals.map((a, i) => (
                <div key={a.id} style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '12px 0',
                  borderBottom: i < accruals.length - 1 ? '1px solid var(--gray-100)' : 'none',
                }}>
                  <div style={{
                    width: 38, height: 38, borderRadius: 10, flexShrink: 0,
                    background: i === 0 ? 'var(--primary-light)' : 'var(--gray-100)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {accrualIcon(a.years_completed)}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--gray-900)' }}>
                      {a.years_completed} año{a.years_completed > 1 ? 's' : ''} cumplido{a.years_completed > 1 ? 's' : ''}
                      <span style={{
                        marginLeft: 8, fontSize: 12, fontWeight: 700, color: 'var(--success)',
                        background: 'var(--success-light)', padding: '1px 7px', borderRadius: 12,
                      }}>
                        +{a.days_added}d
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 2 }}>
                      {format(new Date(a.accrual_date), "d 'de' MMMM, yyyy", { locale: es })}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--primary)' }}>{a.days_balance}d</div>
                    <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>saldo</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Panel derecho */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {profile?.supervisors?.length > 0 && (
            <div className="card">
              <div className="card-header">
                <div className="card-title">Mis supervisores</div>
                <UserCheck size={18} color="var(--gray-400)" />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {profile.supervisors.map(s => (
                  <div key={s.id} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 12px', background: 'var(--gray-50)',
                    borderRadius: 8, border: '1px solid var(--gray-100)',
                  }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: '50%',
                      background: s.is_primary ? 'var(--primary)' : 'var(--gray-300)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: 'white', fontWeight: 600, fontSize: 13, flexShrink: 0,
                    }}>
                      {s.first_name[0]}{s.last_name[0]}
                    </div>
                    <div>
                      <div style={{ fontWeight: 500, fontSize: 14 }}>{s.first_name} {s.last_name}</div>
                      <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                        {s.department}{s.is_primary ? ' · Principal' : ''}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {profile?.vacation_info && (
            <div className="card">
              <div className="card-header">
                <div className="card-title">Mi antigüedad</div>
                <TrendingUp size={18} color="var(--gray-400)" />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {[
                  ['Fecha de ingreso', profile.hire_date
                    ? format(new Date(profile.hire_date), "d 'de' MMMM, yyyy", { locale: es })
                    : 'No registrada'],
                  ['Antigüedad', profile.vacation_info.label || '—'],
                  ['Días acumulados', `${profile.vacation_days_total}`],
                  ['Días utilizados', `${profile.vacation_days_used}`],
                  ['Saldo disponible', `${available}`],
                ].map(([label, value]) => (
                  <div key={label} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '7px 0', borderBottom: '1px solid var(--gray-100)',
                  }}>
                    <span style={{ fontSize: 13, color: 'var(--gray-500)' }}>{label}</span>
                    <span style={{ fontSize: 13, fontWeight: 500 }}>{value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Historial vacaciones */}
      {recentVac.length > 0 && (
        <div className="card section-gap" style={{ marginTop: 20 }}>
          <div className="card-header">
            <div className="card-title">Últimas solicitudes de vacaciones</div>
            <a href="/vacaciones" className="btn btn-secondary btn-sm" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              Ver todas <ChevronRight size={14} />
            </a>
          </div>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr><th>Desde</th><th>Hasta</th><th>Días</th><th>Estado</th><th>Solicitado</th></tr>
              </thead>
              <tbody>
                {recentVac.map(v => (
                  <tr key={v.id}>
                    <td>{format(new Date(v.start_date), 'dd/MM/yyyy')}</td>
                    <td>{format(new Date(v.end_date), 'dd/MM/yyyy')}</td>
                    <td><strong>{v.total_days}</strong></td>
                    <td>{statusBadge(v.status)}</td>
                    <td style={{ color: 'var(--gray-500)', fontSize: 13 }}>
                      {formatDistanceToNow(new Date(v.created_at), { addSuffix: true, locale: es })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {recentLeave.length > 0 && (
        <div className="card" style={{ marginTop: 20 }}>
          <div className="card-header">
            <div className="card-title">Últimos permisos de día libre</div>
            <a href="/permisos" className="btn btn-secondary btn-sm" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              Ver todos <ChevronRight size={14} />
            </a>
          </div>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr><th>Fecha</th><th>Tipo</th><th>Duración</th><th>Motivo</th><th>Estado</th></tr>
              </thead>
              <tbody>
                {recentLeave.map(l => (
                  <tr key={l.id}>
                    <td>{format(new Date(l.start_date), 'dd/MM/yyyy')}</td>
                    <td>{l.type === 'hours' ? 'Horas' : 'Días'}</td>
                    <td>{l.type === 'hours' ? `${l.hours}h` : `${l.days}d`}</td>
                    <td style={{ maxWidth: 200 }} className="truncate">{l.notes}</td>
                    <td>{statusBadge(l.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {recentVac.length === 0 && recentLeave.length === 0 && (
        <div className="card section-gap" style={{ marginTop: 20 }}>
          <div className="empty-state">
            <Palmtree size={40} color="var(--gray-300)" style={{ margin: '0 auto 12px', display: 'block' }} />
            <div className="empty-state-title">Sin solicitudes aún</div>
            <div>Usa el menú para solicitar vacaciones o permisos</div>
          </div>
        </div>
      )}
    </Layout>
  );
}